import UserPrepaidCardApi from '@apis/prepaid/UserPrepaidCardApi';
import AppModal from '@components/AppModal';
import Text from '@components/Text';
import setUserPrepaidCardList from '@hooks/prepaidCard/setUserPrepaidCardList';
import { ErrorResponse } from '@models/error';
import { ActivityComponentType, useFlow } from '@stackflow/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface Params {
    prepaidCardId: string;
}

declare module '@stackflow/config' {
    interface Register {
        DeleteCardModal: Params;
    }
}

const DeleteCardModal: ActivityComponentType<'DeleteCardModal'> = ({ params }) => {
    const { prepaidCardId } = params;
    const { t } = useTranslation('prepaidCard');
    const { pop } = useFlow();
    const queryClient = useQueryClient();
    const { mutate, isPending } = useMutation({
        mutationFn: () => UserPrepaidCardApi.deletePrepaidCard(prepaidCardId),
        onSuccess() {
            pop();
            setUserPrepaidCardList(queryClient, (cards) => cards.filter((card) => card.id !== prepaidCardId));
            toast.success(t('manage.delete.success'));
        },
        onError(e: AxiosError<ErrorResponse>) {
            toast.error(e.response?.data.message || t('manage.delete.error'));
        },
    });

    return (
        <AppModal title={t('manage.delete.confirmTitle')}
            deny={t('manage.delete.cancel')}
            confirm={t('manage.delete.deleteButton')}
            onConfirm={() => mutate()}
            confirmDisabled={isPending}
            denyDisabled={isPending}
            disableOutside={isPending} >
            <Text size='title' align='center' multiline>
                {t('manage.delete.confirmDescription')}
            </Text>
        </AppModal>
    );
};

export default DeleteCardModal;
